import React, { useCallback } from 'react';
import { Button, ButtonSize, ButtonTheme } from '../../shared/common/Button';
import { useTranslation } from 'react-i18next';
import FormatCurrency from '../../shared/utils/formatCurrency';
import { BulkAddProductsToShoppingCartResponse } from '../../store/api';
import { AddOptions } from '../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { useListContentChecks } from '../../shared/common/useListContentChecks';
import { CheckBeforeAddingModal } from '../../shared/common/CheckBeforeAddingModal';
import { UnavailableProductsModal } from '../../shared/common/UnavailableProductsModal';

type Item = {
    readonly totalNetValue?: number;
    readonly netValue?: number;
    readonly basePrice?: number;
    readonly savedValue?: number;
    readonly savedPercentage?: number;
    readonly currency?: string;
    readonly bundleId?: string;
};

type Props<T> = {
    readonly item: T;
    readonly isAddAction?: boolean;
    readonly className?: string;
    readonly onAddToCart?: (bundleId: string, performChecks: boolean) => Promise<BulkAddProductsToShoppingCartResponse>;
    readonly onRemove?: (item: T) => void;
};

function GroupedItemActionButton<T extends Item>({ item, isAddAction, className, onAddToCart, onRemove }: Props<T>) {
    const { t } = useTranslation();
    const { totalNetValue, netValue, savedValue, savedPercentage, basePrice, currency } = item;

    const isDiscounted = !!item.basePrice;
    const customerPrice = totalNetValue || netValue;
    const onAddGroupToCart = useCallback(
        (performChecks: boolean): Promise<BulkAddProductsToShoppingCartResponse> => {
            if (!onAddToCart) {
                throw new Error('technical error');
            }

            return onAddToCart(item.bundleId!, performChecks);
        },
        [item.bundleId, onAddToCart]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<AddOptions>({ onAction: onAddGroupToCart });

    const handleButtonClick = useCallback(() => {
        if (isAddAction) {
            changePrice({ target: ListActionTarget.Cart });
        } else {
            onRemove?.(item);
        }
    }, [isAddAction, changePrice, onRemove, item]);

    const label = isAddAction ? t('addToCart') : t('remove');
    const iconCode = isAddAction ? 'shopping_cart' : 'delete';

    return (
        <div className="bt-line bt-price">
            <Button
                text={label}
                textSecondary={t('numberFormat.numberWithCurrencySymbol', {
                    val: FormatCurrency.convertIntegerToCurrencyFormat(customerPrice),
                    currencySymbol: FormatCurrency.mapCurrencyToSymbol(currency)
                })}
                highlightSecondaryText={isDiscounted}
                icon={iconCode}
                onClick={handleButtonClick}
                size={ButtonSize.SMALL}
                theme={ButtonTheme.NEUTRAL}
                className={className}
            />
            {isDiscounted ? (
                <span className="discount">
                    {t('reserveReports.reserveReport.viewReport.save', {
                        amount: t('numberFormat.numberWithCurrencySymbol', {
                            val: FormatCurrency.convertIntegerToCurrencyFormat(savedValue),
                            currencySymbol: FormatCurrency.mapCurrencyToSymbol(currency)
                        }),
                        percentile: Math.round((savedPercentage ?? 0) / 100)
                    })}
                    <span className="fl-right crossed">
                        {t('numberFormat.numberWithCurrencySymbol', {
                            val: FormatCurrency.convertIntegerToCurrencyFormat(basePrice),
                            currencySymbol: FormatCurrency.mapCurrencyToSymbol(currency)
                        })}
                    </span>
                </span>
            ) : null}
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </div>
    );
}

export default GroupedItemActionButton;
